// HeatPump.js - Bomba de calor (água quente sanitária), via Tuya
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Flame, Thermometer, Power, Activity, Droplets, AlertCircle,
} from 'lucide-react';
import { useTuya } from '../hooks/useTuya';
import ViewHeader from './ViewHeader';

// A gama útil desta bomba. Os saltos rápidos poupam o slider quando se quer
// só "o costume".
const TEMPERATURAS_RAPIDAS = [40, 45, 50, 55, 60, 65];

function HeatPump({ onBack }) {
  const { t } = useTranslation();
  const { devices, loading, error, fetchDevices, control, togglePower } = useTuya();

  const bomba = devices.find((d) => d.device_type === 'heatpump');
  const definirTemperatura = (temp) => control(bomba.device_id, { temp_set: parseInt(temp, 10) });

  return (
    <div className="app-container">
      <div className="app-content">
        <ViewHeader
          icon={Flame}
          iconClass="icon-heatpump"
          titulo={t('bombaCalor.titulo')}
          subtitulo={t('bombaCalor.subtitulo')}
          onBack={onBack}
          onRefresh={fetchDevices}
          loading={loading}
        />

        {error && (
          <div className="error-banner">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {!bomba ? (
          <div className="loading-container">
            <div className="loading-spinner" />
            <p>{t('bombaCalor.aCarregar')}</p>
          </div>
        ) : (
          <div className="heatpump-container">
            <div className={`heatpump-status-card ${bomba.online ? 'online' : 'offline'}`}>
              <div className="heatpump-status-header">
                <div className="status-indicator">
                  <div className={`status-dot ${bomba.online ? 'online' : 'offline'}`} />
                  <span>{bomba.online ? t('comum.online') : t('comum.offline')}</span>
                </div>
                <div className={`power-badge ${bomba.power ? 'on' : 'off'}`}>
                  {bomba.power ? t('comum.ligadoMaiusc') : t('comum.desligadoMaiusc')}
                </div>
              </div>

              <div className="temp-display-section">
                <div className="temp-display-main">
                  <Thermometer size={48} className="temp-icon" />
                  <div className="temp-reading">
                    <span className="temp-value">{bomba.temp_current}°</span>
                    <span className="temp-label">{t('bombaCalor.temperaturaAtual')}</span>
                  </div>
                </div>

                <div className="temp-divider" />

                <div className="temp-display-target">
                  <Flame size={40} className="target-icon" />
                  <div className="temp-reading">
                    <span className="temp-value">{bomba.temp_set}°</span>
                    <span className="temp-label">{t('bombaCalor.temperaturaAlvo')}</span>
                  </div>
                </div>
              </div>

              <div className="temp-progress">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${((bomba.temp_current - bomba.temp_min) / (bomba.temp_max - bomba.temp_min)) * 100}%`,
                    }}
                  />
                </div>
                <div className="progress-labels">
                  <span>{bomba.temp_min}°C</span>
                  <span>{bomba.temp_max}°C</span>
                </div>
              </div>
            </div>

            <div className="heatpump-controls-card">
              <h3>{t('comum.controlos')}</h3>

              <div className="control-section">
                <div className="control-header">
                  <Power size={20} />
                  <span>{t('comum.alimentacao')}</span>
                </div>
                <button
                  className={`btn-power ${bomba.power ? 'on' : 'off'}`}
                  onClick={() => togglePower(bomba.device_id, bomba.power)}
                  disabled={loading || !bomba.online}
                >
                  <Power size={24} />
                  <span>{bomba.power ? t('comum.desligar') : t('comum.ligar')}</span>
                </button>
              </div>

              <div className="control-section">
                <div className="control-header">
                  <Thermometer size={20} />
                  <span>{t('bombaCalor.ajustarTemperatura')}</span>
                </div>

                <div className="temp-slider-container">
                  <input
                    type="range"
                    min={bomba.temp_min}
                    max={bomba.temp_max}
                    value={bomba.temp_set}
                    onChange={(e) => definirTemperatura(e.target.value)}
                    className="temp-slider"
                    disabled={loading || !bomba.online || !bomba.power}
                  />

                  <div className="temp-quick-buttons">
                    {TEMPERATURAS_RAPIDAS.map((temp) => (
                      <button
                        key={temp}
                        className={`temp-quick-btn ${bomba.temp_set === temp ? 'active' : ''}`}
                        onClick={() => definirTemperatura(temp)}
                        disabled={loading || !bomba.online || !bomba.power}
                      >
                        {temp}°
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="heatpump-info-card">
              <h3>{t('comum.informacoes')}</h3>
              <div className="info-grid">
                <div className="info-item">
                  <Activity size={20} />
                  <div>
                    <span className="info-label">{t('comum.estado')}</span>
                    <span className="info-value">
                      {bomba.power ? t('bombaCalor.aAquecer') : t('bombaCalor.emStandby')}
                    </span>
                  </div>
                </div>
                <div className="info-item">
                  <Thermometer size={20} />
                  <div>
                    <span className="info-label">{t('bombaCalor.diferencial')}</span>
                    <span className="info-value">
                      {Math.abs(bomba.temp_set - bomba.temp_current)}°C
                    </span>
                  </div>
                </div>
                <div className="info-item">
                  <Droplets size={20} />
                  <div>
                    <span className="info-label">{t('comum.tipo')}</span>
                    <span className="info-value">{t('bombaCalor.aguaQuente')}</span>
                  </div>
                </div>
                <div className="info-item">
                  <Flame size={20} />
                  <div>
                    <span className="info-label">{t('comum.modelo')}</span>
                    <span className="info-value">WJL139005</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default HeatPump;
